import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, X } from 'lucide-react';
import { PocketBaseDataService as DataService } from '../services/pocketbaseDataService';

// Localized messages for confirmation page
const confirmationMessages: Record<string, {
  confirming: string;
  pleaseWait: string;
  success: string;
  successMessage: string;
  failed: string;
  failedMessage: string;
  closeTab: string;
}> = {
  en: {
    confirming: 'Confirming your subscription...',
    pleaseWait: 'Please wait while we verify your email address.',
    success: 'Subscription Confirmed!',
    successMessage: 'Thank you for confirming your email address. You\'re now subscribed to our newsletter and will receive updates about our latest offers.',
    failed: 'Confirmation Failed',
    failedMessage: 'We could not confirm your subscription. The link may have expired or already been used.',
    closeTab: 'Close Tab'
  },
  bg: {
    confirming: 'Потвърждаване на абонамента...',
    pleaseWait: 'Моля, изчакайте докато потвърдим вашия имейл адрес.',
    success: 'Абонаментът е потвърден!',
    successMessage: 'Благодарим ви за потвърждението на имейл адреса. Вече сте абонирани за нашия бюлетин и ще получавате актуализации за най-новите ни оферти.',
    failed: 'Потвърждението не бе успешно',
    failedMessage: 'Не успяхме да потвърдим абонамента ви. Връзката може да е изтекла или вече да е използвана.',
    closeTab: 'Затвори таба'
  },
  es: {
    confirming: 'Confirmando tu suscripción...',
    pleaseWait: 'Por favor espera mientras verificamos tu dirección de correo electrónico.',
    success: '¡Suscripción Confirmada!',
    successMessage: 'Gracias por confirmar tu dirección de correo electrónico. Ahora estás suscrito a nuestro boletín y recibirás actualizaciones sobre nuestras últimas ofertas.',
    failed: 'Confirmación Fallida',
    failedMessage: 'No pudimos confirmar tu suscripción. El enlace puede haber expirado o ya haber sido utilizado.',
    closeTab: 'Cerrar Pestaña'
  },
  fr: {
    confirming: 'Confirmation de votre abonnement...',
    pleaseWait: 'Veuillez patienter pendant que nous vérifions votre adresse e-mail.',
    success: 'Abonnement Confirmé!',
    successMessage: 'Merci d\'avoir confirmé votre adresse e-mail. Vous êtes maintenant abonné à notre newsletter et recevrez des mises à jour sur nos dernières offres.',
    failed: 'Échec de la Confirmation',
    failedMessage: 'Nous n\'avons pas pu confirmer votre abonnement. Le lien a peut-être expiré ou a déjà été utilisé.',
    closeTab: 'Fermer l\'onglet'
  }
};

export const ConfirmSubscription: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [locale, setLocale] = useState('en');
  const hasConfirmed = useRef(false);

  // Get localized messages
  const messages = confirmationMessages[locale] || confirmationMessages.en;

  const handleCloseTab = () => {
    window.close();
    // Fallback if window.close() doesn't work (some browsers block it)
    // Show a message instead
  };

  useEffect(() => {
    const confirmEmail = async () => {
      // Prevent double submission (React StrictMode or double-click)
      if (hasConfirmed.current) return;
      hasConfirmed.current = true;
      
      const token = searchParams.get('token');
      const localeParam = searchParams.get('locale') || 'en';
      setLocale(localeParam);
      
      if (!token) {
        setStatus('error');
        setErrorMessage('No confirmation token provided.');
        return;
      }

      const { error } = await DataService.confirmSubscriber(token);
      
      if (error) {
        setStatus('error');
        setErrorMessage(error);
      } else {
        setStatus('success');
      }
    };

    confirmEmail();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="bg-blue-100 p-4 rounded-full">
                <Loader2 className="text-blue-600 animate-spin" size={48} />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {messages.confirming}
            </h1>
            <p className="text-gray-600">
              {messages.pleaseWait}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="bg-green-100 p-4 rounded-full">
                <CheckCircle className="text-green-600" size={48} />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {messages.success}
            </h1>
            <p className="text-gray-600 mb-6">
              {messages.successMessage}
            </p>
            <div className="flex justify-center">
              <button
                onClick={handleCloseTab}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition duration-200"
              >
                <X className="mr-2" size={20} />
                {messages.closeTab}
              </button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="bg-red-100 p-4 rounded-full">
                <XCircle className="text-red-600" size={48} />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {messages.failed}
            </h1>
            <p className="text-gray-600 mb-6">
              {errorMessage || messages.failedMessage}
            </p>
            <div className="flex justify-center">
              <button
                onClick={handleCloseTab}
                className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition duration-200"
              >
                <X className="mr-2" size={20} />
                {messages.closeTab}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
